/**
 * UserMenu — Dropdown menu with avatar, name, role, logout
 *
 * Uses the Radix-based shadcn DropdownMenu (pointer-event driven, portalled,
 * with proper focus management) rather than a CSS `:focus-within` trick.
 * The old implementation relied on `group-focus-within` to reveal the menu,
 * but WebKit (iOS Safari + Safari on macOS) does NOT move focus to a <button>
 * on tap/click, so the menu never opened there — blocking sign-out for iPhone
 * users. Radix listens on pointer events and manages an explicit open state,
 * which works consistently across iOS Safari, desktop Safari, and Android.
 */

import { useNavigate } from 'react-router-dom';
import { LogOut, User, Wallet } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';

export function UserMenu() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { profile, role } = useAuthStore();

  const displayFirst = profile?.display_first_name || profile?.first_name || '';
  const displayLast = profile?.display_last_name || profile?.last_name || '';
  const displayName = displayFirst || profile?.primary_email || 'User';
  const fullDisplayName = [displayFirst, displayLast].filter(Boolean).join(' ') || displayName;
  const initials = fullDisplayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const roleName = role?.role_name || role?.role_type || 'User';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    // modal={false} disables Radix's scroll-lock (react-remove-scroll). The
    // default modal lock sets `body[data-scroll-locked]{overflow:hidden;
    // position:relative}`, which turns <body> into a scroll container and
    // steals the scrollport from the `position: sticky` AppHeader — so when the
    // page is scrolled, opening this menu pinned the header to the un-scrolled
    // body and slid it out of view. Non-modal keeps open/close, outside-click,
    // Escape, and item navigation working without touching the scroll context.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className="flex items-center gap-2 rounded-md border border-transparent p-1.5 hover:bg-white/10 focus-visible:outline-none transition-colors [touch-action:manipulation]"
        aria-label="User menu"
      >
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt={displayName}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
            {initials}
          </div>
        )}
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium leading-tight truncate max-w-[120px]">
            {displayName}
          </p>
          <p className="text-xs opacity-60 capitalize">{roleName}</p>
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <div className="p-2 border-b border-border">
          <p className="text-sm font-medium text-foreground truncate">{fullDisplayName}</p>
          <p className="text-xs text-muted-foreground truncate">{profile?.primary_email}</p>
          <span className="mt-1 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary capitalize">
            {roleName}
          </span>
        </div>

        <DropdownMenuItem onSelect={() => navigate('/profile')}>
          <User className="h-4 w-4" />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/profile?tab=funds')}>
          <Wallet className="h-4 w-4" />
          Funds
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => {
            void handleLogout();
          }}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
